import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../services/api'; 

const EventDetail = () => {
    const { id } = useParams(); 
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);

    const BACKEND_URL = "https://sherianalumni.com/api/";

    useEffect(() => {
        API.get(`/get_event_detail.php?id=${id}`)
            .then(res => {
                if (res.data && !res.data.error) {
                    setEvent(res.data);
                } else {
                    setEvent(null);
                }
                setLoading(false);
            })
            .catch(err => {
                console.error("Error fetching event details:", err);
                setLoading(false);
            });
    }, [id]);

    // 🌟 AESTHETIC SCROLL REVEAL LOGIC
    useEffect(() => {
        if (!loading && event) {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('is-revealed');
                        observer.unobserve(entry.target); 
                    }
                });
            }, { threshold: 0.1, rootMargin: "0px 0px -20px 0px" });

            const revealElements = document.querySelectorAll('.reveal-elem');
            revealElements.forEach((el) => observer.observe(el));

            return () => observer.disconnect();
        }
    }, [loading, event]);

    const getEventImage = (imagePath) => {
        if (!imagePath) return "";
        if (imagePath.startsWith('http')) return imagePath;
        let cleanPath = imagePath.replace(/^(\.\.\/api\/|\/api\/|\/)/, ''); 
        cleanPath = cleanPath.startsWith('api/') ? cleanPath.substring(4) : cleanPath;
        return encodeURI(`${BACKEND_URL}${cleanPath}`); 
    };

    if (loading) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
                <h2 style={{ color: '#001d3d', animation: 'pulse 1.5s infinite' }}>Loading Dispatch...</h2>
                <style>{`@keyframes pulse { 0% { opacity: 0.5; } 50% { opacity: 1; } 100% { opacity: 0.5; } }`}</style>
            </div>
        );
    }

    if (!event) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
                <h2 style={{ color: '#001d3d', marginBottom: '20px', fontWeight: 900 }}>Event Not Found</h2>
                <button className="btn-back" onClick={() => navigate(-1)} style={{background: '#ffc300', color: '#001d3d', border: 'none', padding: '15px 40px', borderRadius: '50px', fontWeight: 'bold', cursor: 'pointer'}}>Return to Base</button>
            </div>
        );
    }

    const eventDate = new Date(event.event_date);
    const formattedDate = eventDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

    return (
        <div className="event-detail-wrapper">
           <style>{`
    .event-detail-wrapper {
        background: #f8fafc;
        min-height: 100vh;
        font-family: 'Inter', -apple-system, sans-serif;
        padding-bottom: 100px;
    }
    
    /* 🌟 AESTHETIC ANIMATION CLASSES */
    .reveal-elem {
        opacity: 0;
        transform: translateY(30px);
        transition: all 1.2s cubic-bezier(0.22, 1, 0.36, 1);
    }
    .reveal-elem.is-revealed {
        opacity: 1;
        transform: translateY(0);
    }
    
    /* FULL WIDTH HERO IMAGE */
    .event-detail-hero {
        width: 100%;
        height: 55vh; 
        min-height: 450px;
        background: #0c2d19; /* 🌟 Updated to Dark Green */
        position: relative;
        margin-top: 80px; 
        overflow: hidden;
    }
    
    .event-detail-hero img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: top center;
        animation: subtleZoomIn 20s infinite alternate ease-in-out;
    }

    .hero-gradient {
        position: absolute;
        bottom: 0; left: 0; right: 0;
        height: 250px;
        /* 🌟 Updated to Green Gradient */
        background: linear-gradient(to top, rgba(19, 70, 40, 0.85), transparent);
    }

    /* 🌟 FIXED: FLOATING DATE BADGE */
    .detail-date-badge {
        position: absolute;
        bottom: 40px;
        left: 50%;
        transform: translateX(-50%);
        background: #ffc300;
        color: #134628; /* 🌟 Updated to Green */
        padding: 15px 45px;
        border-radius: 50px;
        font-weight: 900;
        font-size: 1.2rem;
        box-shadow: 0 15px 35px rgba(19, 70, 40, 0.5); /* Updated Shadow */
        border: 4px solid #ffffff;
        z-index: 10;
        white-space: nowrap;
        animation: badgePopUp 1s cubic-bezier(0.175, 0.885, 0.32, 1.275) 0.5s backwards;
    }

    /* CLEAN EDITORIAL TEXT CONTAINER */
    .event-content-section {
        max-width: 950px;
        margin: 60px auto 0;
        padding: 50px;
        background: #ffffff;
        border-radius: 24px;
        box-shadow: 0 20px 50px rgba(19, 70, 40, 0.05); /* Updated Shadow */
        border: 1px solid rgba(19, 70, 40, 0.05); /* Updated */
    }
    
    .event-title {
        color: #134628; /* 🌟 Updated to Green */
        font-weight: 900;
        font-size: clamp(2.5rem, 5vw, 3.5rem);
        margin-bottom: 20px;
        line-height: 1.1;
        letter-spacing: -1px;
    }
    
    .event-meta {
        color: #64748b;
        font-size: 1rem;
        font-weight: 600;
        margin-bottom: 40px;
        display: flex;
        align-items: center;
        gap: 15px;
        border-bottom: 2px solid rgba(255, 195, 0, 0.3);
        padding-bottom: 25px;
        text-transform: uppercase;
        letter-spacing: 1px;
    }
    
    .event-description {
        color: #334155;
        font-size: 1.2rem;
        line-height: 2.1;
        white-space: pre-wrap; 
        font-weight: 400;
    }
    
    /* PRESTIGE BUTTON */
    .btn-back-container {
        margin-top: 60px;
        text-align: center;
    }

    .btn-back {
        background: #134628; /* 🌟 Updated to Green */
        color: #ffc300;
        border: none;
        padding: 18px 45px;
        border-radius: 50px;
        font-weight: 800;
        font-size: 1.1rem;
        text-transform: uppercase;
        cursor: pointer;
        transition: 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        display: inline-flex;
        align-items: center;
        gap: 12px;
        box-shadow: 0 10px 20px rgba(19, 70, 40, 0.15); /* Updated Shadow */
    }
    
    .btn-back:hover {
        background: #ffc300;
        color: #134628; /* 🌟 Updated to Green */
        transform: translateY(-5px);
        box-shadow: 0 15px 30px rgba(255, 195, 0, 0.3);
    }
    
    /* KEYFRAMES */
    @keyframes subtleZoomIn { 0% { transform: scale(1); } 100% { transform: scale(1.1); } }
    @keyframes badgePopUp { 
        0% { opacity: 0; transform: translate(-50%, 50px) scale(0.9); } 
        100% { opacity: 1; transform: translate(-50%, 0) scale(1); } 
    }

    /* MOBILE RESPONSIVE */
    @media (max-width: 768px) {
        .event-detail-hero { height: 40vh; min-height: 300px; }
        .detail-date-badge { padding: 12px 30px; font-size: 1rem; bottom: 20px; border-width: 3px; }
        .event-content-section { margin-top: 40px; padding: 30px 20px; border-radius: 0; border-left: none; border-right: none; }
    }
`}</style>

            {/* FULL WIDTH HERO */}
            <div className="event-detail-hero">
                {event.event_image ? (
                    <img 
                        src={getEventImage(event.event_image)} 
                        alt={event.title} 
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none'; 
                        }}
                    />
                ) : (
                    <div style={{width: '100%', height: '100%', display:'flex', alignItems:'center', justifyContent:'center'}}>
                        <i className="fas fa-image" style={{fontSize: '5rem', color: 'rgba(255,195,0,0.1)'}}></i>
                    </div>
                )}
                
                <div className="hero-gradient"></div>

                {/* 🌟 FIXED: FLOATING DATE BADGE */}
                <div className="detail-date-badge">
                    <i className="far fa-calendar-alt me-2" style={{color: '#001d3d'}}></i> {formattedDate}
                </div>
            </div>

            {/* EDITORIAL CONTENT SECTION */}
            <div className="event-content-section">
                <h1 className="event-title reveal-elem">{event.title}</h1>
                
                <div className="event-meta reveal-elem" style={{transitionDelay: '0.1s'}}>
                    <span><i className="fas fa-clock me-2" style={{color: '#ffc300', fontSize: '1.2rem'}}></i> Dispatch Date: {new Date(event.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                </div>

                <div className="event-description reveal-elem" style={{transitionDelay: '0.2s'}}>
                    {event.description}
                </div>

                <div className="btn-back-container reveal-elem" style={{transitionDelay: '0.3s'}}>
                    <button className="btn-back" onClick={() => navigate(-1)}>
                        <i className="fas fa-arrow-left"></i> Return to Feed
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EventDetail;